import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { EstadoEditorial } from '../api';

/**
 * api.ts — las rutas de aprendizaje, `/api/learning-paths`. Módulo 11, opción 3.
 *
 * Contrato del vanilla, llamada a llamada, salvo una cosa: los cursos que se
 * pueden añadir a una ruta salen de `useCursos` del catálogo, con su tope de
 * 200. El vanilla pedía `/courses/?limit=100`, y en una empresa con más de cien
 * cursos el 101 no se podía añadir a ninguna ruta —sin aviso: no salía en la
 * lista—.
 *
 * ## Vaciar un filtro no es mandar `null`
 *
 * El `PUT /learning-paths/{id}` trata `null` como «no lo toques», y solo vacía un
 * campo si le llega algo falso: `0` para un id, `""` para un texto. El formulario
 * vanilla mandaba `null` al elegir «Cualquier área», así que **un filtro de
 * audiencia, una vez puesto, no se podía quitar**: el servidor respondía «Ruta
 * actualizada», la ficha se volvía a pintar y el área seguía ahí. Aquí el
 * formulario de edición manda el vacío que el servidor entiende; lo fija
 * `test_formacion.py::test_editar_una_ruta_vacia_un_filtro_con_cero_y_no_con_null`.
 */

/** Un paso de la ruta, tal como lo devuelve `GET /learning-paths/{id}`. */
export interface PasoDeRuta {
  /** El id de la FILA de la ruta, no el del curso: es el que piden reordenar,
   *  marcar como requerido y quitar. */
  lpc_id: number;
  course_id: number;
  order: number;
  required: boolean;
  title: string;
  course_type?: string | null;
  cover_icon?: string | null;
  cover_icon_color?: string | null;
  lesson_count: number;
  level?: string | null;
  enrolled: boolean;
  progress_pct: number;
  completed: boolean;
  /** Lo calcula el servidor: en una ruta secuencial, un paso se abre al
   *  terminar los requeridos anteriores. Nunca está bloqueado para gestión. */
  locked: boolean;
}

export interface RutaResumen {
  id: number;
  title: string;
  description?: string | null;
  area_id?: number | null;
  target_role?: string | null;
  target_position?: string | null;
  is_mandatory: boolean;
  sequential: boolean;
  status: EstadoEditorial | string;
  cover_icon?: string | null;
  cover_icon_color?: string | null;
  course_count: number;
  required_count: number;
  completed_count: number;
  progress_pct: number;
  completed: boolean;
}

export interface RutaDetalle extends RutaResumen {
  courses: PasoDeRuta[];
  area_name?: string | null;
  company_id?: number | null;
  company_name?: string | null;
  target_role_label?: string | null;
  target_user_count: number;
}

export interface PersonaDeLaRuta {
  id: number;
  full_name: string;
  position?: string | null;
}

/**
 * Lo que manda el formulario. En el alta, `null` es «sin filtro»; en la
 * edición, «sin filtro» es `0` o `""` —ver arriba—, y el formulario decide.
 */
export interface CuerpoDeRuta {
  title: string;
  description: string | null;
  area_id: number | null;
  target_role: string | null;
  target_position: string | null;
  company_id: number | null;
  cover_icon: string | null;
  cover_icon_color: string;
  sequential: boolean;
  is_mandatory: boolean;
  status?: EstadoEditorial;
}

export const clavesDeRutas = {
  /** El prefijo de todo: cualquier cambio en una ruta mueve la lista y el detalle. */
  todas: ['formacion', 'rutas'] as const,
  lista: (archivadas: boolean) => ['formacion', 'rutas', 'lista', archivadas] as const,
  ruta: (id: number) => ['formacion', 'rutas', 'ruta', id] as const,
  personas: (id: number) => ['formacion', 'rutas', 'ruta', id, 'personas'] as const,
  empresas: ['formacion', 'empresas'] as const,
};

/* ── Lecturas ─────────────────────────────────────────────────────────── */

/**
 * La lista. Sin `status`, a quien gestiona le llegan borradores y publicadas;
 * **las archivadas solo con `?status=archivado`**. El vanilla nunca lo pedía, así
 * que archivar una ruta era un viaje de ida: desaparecía de la lista y solo se
 * podía volver a ella con el enlace. Aquí es un filtro de quien gestiona.
 */
export function useRutas(archivadas: boolean) {
  return useQuery({
    queryKey: clavesDeRutas.lista(archivadas),
    queryFn: () =>
      api.get<RutaResumen[]>('/learning-paths/', archivadas ? { status: 'archivado' } : undefined),
  });
}

export function useRuta(id: number | null) {
  return useQuery({
    queryKey: clavesDeRutas.ruta(id ?? 0),
    queryFn: () => api.get<RutaDetalle>(`/learning-paths/${id}`),
    enabled: id != null,
  });
}

export function usePersonasDeLaRuta(id: number, habilitado: boolean) {
  return useQuery({
    queryKey: clavesDeRutas.personas(id),
    queryFn: () => api.get<PersonaDeLaRuta[]>(`/learning-paths/${id}/target-users`),
    enabled: habilitado,
  });
}

/** `StaffRead` en el servidor: la pide el formulario, que solo ve gestión. */
export function useEmpresas(habilitado: boolean) {
  return useQuery({
    queryKey: clavesDeRutas.empresas,
    queryFn: () => api.get<{ items?: Array<{ id: number; name: string }> }>('/admin/catalogs/companies'),
    select: (r) => r.items ?? [],
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

/* ── Escrituras ───────────────────────────────────────────────────────── */

/**
 * Tras cualquier cambio, la lista y el detalle a la vez: el panel de gestión y
 * la página de detalle leen la MISMA consulta. En el vanilla el panel se
 * repintaba solo a sí mismo, y al cerrarlo la página de detrás seguía enseñando
 * la secuencia de antes.
 *
 * Devuelve la promesa a propósito: la mutación no se da por terminada hasta que
 * los datos nuevos han llegado, así que sus botones siguen ocupados mientras
 * tanto y el siguiente clic parte de lo que de verdad hay en el servidor.
 */
function useRefrescarRutas() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: clavesDeRutas.todas });
}

export function useCrearRuta() {
  const refrescar = useRefrescarRutas();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeRuta) => api.post<{ id: number }>('/learning-paths/', cuerpo),
    onSuccess: refrescar,
  });
}

/** Editar los datos, o solo el estado —publicar, archivar, restaurar—. */
export function useEditarRuta() {
  const refrescar = useRefrescarRutas();
  return useMutation({
    mutationFn: (v: { id: number; cuerpo: Partial<CuerpoDeRuta> }) =>
      api.put<{ message?: string }>(`/learning-paths/${v.id}`, v.cuerpo),
    onSuccess: refrescar,
  });
}

/** Sin refrescar: quien la llama navega a la lista, y cada navegación monta la
 *  capa React con una caché nueva. Refrescar aquí volvería a pedir la ruta
 *  recién borrada y pintaría un 404 durante el instante que tarda en irse. */
export function useBorrarRuta() {
  return useMutation({
    mutationFn: (id: number) => api.del<{ message?: string }>(`/learning-paths/${id}`),
  });
}

export function useAgregarCurso() {
  const refrescar = useRefrescarRutas();
  return useMutation({
    mutationFn: (v: { id: number; course_id: number; required: boolean }) =>
      api.post<{ id: number }>(`/learning-paths/${v.id}/courses`, {
        course_id: v.course_id,
        required: v.required,
      }),
    onSuccess: refrescar,
  });
}

/**
 * Marcar un paso como requerido o no.
 *
 * El vanilla cambiaba la casilla al pulsar y, si el servidor fallaba, sacaba un
 * toast y **la dejaba cambiada**: la pantalla decía una cosa y la base otra. Y no
 * refrescaba nada, aunque lo requerido decide el avance de la ruta y qué pasos
 * se bloquean. Aquí la casilla refleja lo que hay en el servidor, se refresca al
 * terminar pase lo que pase, y queda deshabilitada mientras tanto.
 */
export function useCambiarRequerido() {
  const refrescar = useRefrescarRutas();
  return useMutation({
    mutationFn: (v: { id: number; lpc_id: number; required: boolean }) =>
      api.put<{ message?: string }>(`/learning-paths/${v.id}/courses/${v.lpc_id}`, { required: v.required }),
    onSettled: refrescar,
  });
}

export function useQuitarCurso() {
  const refrescar = useRefrescarRutas();
  return useMutation({
    mutationFn: (v: { id: number; lpc_id: number }) =>
      api.del<{ message?: string }>(`/learning-paths/${v.id}/courses/${v.lpc_id}`),
    onSuccess: refrescar,
  });
}

/** El orden entero, como lista de `lpc_id`: es lo que pide el servidor. */
export function useReordenar() {
  const refrescar = useRefrescarRutas();
  return useMutation({
    mutationFn: (v: { id: number; orden: number[] }) =>
      api.put<{ message?: string }>(`/learning-paths/${v.id}/reorder`, v.orden),
    onSettled: refrescar,
  });
}

/**
 * Sustituye la lista entera de personas, igual que el vanilla —el `PUT` no
 * añade ni quita de una en una—.
 *
 * El vanilla tocaba su lista local ANTES de preguntar al servidor: si el `PUT`
 * fallaba, la persona ya estaba añadida o quitada en memoria, y el siguiente
 * cambio que sí se guardaba arrastraba el que había fallado. Aquí la lista es la
 * del servidor y solo cambia cuando el servidor lo confirma.
 *
 * Y como cada `PUT` lleva la lista entera, dos cambios seguidos calculados sobre
 * la misma lista se pisan: quitar a dos personas deprisa volvía a añadir a la
 * primera. Por eso la mutación sigue ocupada hasta que la lista nueva ha
 * llegado, y la pantalla no deja cambiar nada mientras tanto.
 */
export function useGuardarPersonas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; user_ids: number[] }) =>
      api.put<{ message?: string; count?: number }>(`/learning-paths/${v.id}/target-users`, {
        user_ids: v.user_ids,
      }),
    // El detalle también cambia: enseña cuántas personas hay asignadas.
    onSuccess: (_d, v) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: clavesDeRutas.personas(v.id) }),
        qc.invalidateQueries({ queryKey: clavesDeRutas.ruta(v.id) }),
      ]),
  });
}

/* ── Ir a la gestión de una ruta recién creada ────────────────────────── */

/**
 * El vanilla creaba la ruta, navegaba a su detalle y abría el panel de gestión
 * con un `setTimeout(…, 400)`: si el detalle tardaba más en pintarse, el panel
 * se abría antes y el detalle lo tapaba. Aquí la lista deja dicho qué ruta hay
 * que gestionar, y el detalle lo recoge al montar —la navegación desmonta la
 * capa React entera, pero este módulo sigue cargado—.
 */
let gestionarAlAbrir: number | null = null;

export function pedirGestionAlAbrir(id: number) {
  gestionarAlAbrir = id;
}

export function tomarGestionPendiente(id: number): boolean {
  const toca = gestionarAlAbrir === id;
  gestionarAlAbrir = null;
  return toca;
}
